import { UPLOAD_RESOURCES, UPLOAD_TYPES } from "../model/model";

/**
 * Règles d'upload côté interface, appliquées avant tout envoi.
 *
 * Miroir de zorni-controller (src/upload/upload-file.policy.ts) : mêmes
 * extensions et même taille maximale. Le backend reste la référence — il
 * revérifie le contenu, la taille et le type. Ici on refuse tôt, sans
 * aller-retour réseau.
 */

// Taille maximale d'un fichier. Garder identique à MAX_UPLOAD_FILE_SIZE_MB
// côté controller.
export const MAX_UPLOAD_FILE_SIZE_MB = 10;
export const MAX_UPLOAD_FILE_SIZE = MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024;

// Uploads standards (photos de voyage et d'organisme, documents).
export const STANDARD_UPLOAD_EXTENSIONS = ["jpeg", "jpg", "png", "pdf"];

// Photo de profil (modal de création et modification de profil) : pas de PDF.
export const PROFILE_PHOTO_UPLOAD_EXTENSIONS = ["jpeg", "jpg", "png"];

const MIME_TYPES_BY_EXTENSION = {
  jpeg: ["image/jpeg", "image/jpg", "image/pjpeg"],
  jpg: ["image/jpeg", "image/jpg", "image/pjpeg"],
  png: ["image/png"],
  pdf: ["application/pdf", "application/x-pdf"],
};

// Type générique envoyé par certains navigateurs : on ne refuse pas dessus.
const GENERIC_MIME_TYPE = "application/octet-stream";

/** Extensions acceptées pour un couple (ressource, type), comme côté backend. */
export const getAllowedUploadExtensions = (resourceType, type) =>
  resourceType === UPLOAD_RESOURCES.PERSON && type === UPLOAD_TYPES.PHOTO
    ? PROFILE_PHOTO_UPLOAD_EXTENSIONS
    : STANDARD_UPLOAD_EXTENSIONS;

/** Valeur de l'attribut `accept` d'un champ fichier : ".jpeg,.jpg,.png,.pdf". */
export const getAcceptAttribute = (extensions) =>
  extensions.map((extension) => `.${extension}`).join(",");

export const getFileExtension = (name = "") => {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
};

export const formatUploadFormats = (extensions) =>
  extensions.map((extension) => extension.toUpperCase()).join(", ");

/** Texte de repli des règles, sans i18n (utilisé quand aucune traduction n'est disponible). */
export const describeUploadRules = (extensions) =>
  `Formats acceptés : ${formatUploadFormats(extensions)}. Taille maximale : ${MAX_UPLOAD_FILE_SIZE_MB} Mo.`;

/**
 * Motif de refus d'un fichier : "size", "extension", "type", ou null si le
 * fichier est acceptable. Même ordre que le backend : taille, extension, type
 * MIME déclaré.
 */
export const getUploadFileRejection = (file, extensions) => {
  if (file.size > MAX_UPLOAD_FILE_SIZE) return "size";

  const extension = getFileExtension(file.name);
  if (!extensions.includes(extension)) return "extension";

  const mimeType = (file.type || "").toLowerCase();
  if (
    mimeType &&
    mimeType !== GENERIC_MIME_TYPE &&
    !(MIME_TYPES_BY_EXTENSION[extension] ?? []).includes(mimeType)
  ) {
    return "type";
  }

  return null;
};

const buildRejectionMessage = (reason, extensions, t) => {
  const reasonText = {
    extension: t("uploadRules.errors.extension", "Format non autorisé."),
    size: t("uploadRules.errors.size", "Fichier trop volumineux."),
    type: t("uploadRules.errors.type", "Type de fichier non autorisé."),
  }[reason];

  const rules = t(
    "uploadRules.errors.allowed",
    "Formats acceptés : {{formats}}. Taille maximale : {{maxSizeMb}} Mo.",
    {
      formats: formatUploadFormats(extensions),
      maxSizeMb: MAX_UPLOAD_FILE_SIZE_MB,
    }
  );

  return `${reasonText} ${rules}`;
};

/**
 * Sépare les fichiers acceptés des refusés. Renvoie le message d'erreur du
 * premier refus (formats acceptés et taille maximale inclus), ou null.
 */
export const filterUploadFiles = (files, { extensions, t }) => {
  const accepted = [];
  let errorMessage = null;

  files.forEach((file) => {
    const reason = getUploadFileRejection(file, extensions);
    if (reason) {
      errorMessage = errorMessage || buildRejectionMessage(reason, extensions, t);
      return;
    }
    accepted.push(file);
  });

  return { files: accepted, errorMessage };
};
